import { useState } from 'react';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

// Legacy JS api module — typed via legacy-shims.d.ts.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import * as wildfireReportsApi from '@/api/wildfireReports';

interface EmailReportDialogProps {
  wildfireId: number;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal opened from a wildfire detail page that lets staff email the
 * incident PDF to one or more external recipients (insurance brokers,
 * municipal officials, etc.). The backend renders the PDF via
 * WeasyPrint and dispatches via SES.
 *
 * Recipients are parsed as comma- or whitespace-separated. We deliberately
 * keep validation light: a malformed email surfaces as a server-side 422
 * which we render inline. The PDF itself is server-side so the client
 * doesn't need to know how to compose it.
 */
export default function EmailReportDialog({
  wildfireId,
  open,
  onClose,
}: EmailReportDialogProps) {
  const [recipientsInput, setRecipientsInput] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const parseRecipients = (raw: string): string[] =>
    raw
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSend = async () => {
    const recipients = parseRecipients(recipientsInput);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient email.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await wildfireReportsApi.emailWildfireReport(wildfireId, {
        recipients,
        note: note.trim() || null,
      });
      toast.success(
        `Report sent to ${result.recipients.length} recipient${
          result.recipients.length === 1 ? '' : 's'
        }`,
      );
      setRecipientsInput('');
      setNote('');
      onClose();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = err as any;
      toast.error(
        e?.response?.data?.detail || e?.message || 'Failed to send report',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Email incident report</DialogTitle>
          <DialogDescription>
            We&apos;ll render a PDF of the incident and send it to the
            addresses below. Each recipient gets the same PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="recipients">Recipients</Label>
            <Input
              id="recipients"
              autoFocus
              placeholder="broker@insurer.com, council@town.gov"
              value={recipientsInput}
              onChange={(e) => setRecipientsInput(e.target.value)}
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated. Up to 20 addresses per send.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="note">Cover note (optional)</Label>
            <Textarea
              id="note"
              rows={4}
              placeholder="Anything you'd like the recipient to know before opening the PDF…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={submitting} className="gap-2">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send report
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
